import React, { useState, useEffect } from "react";
import { Smartphone, ToggleLeft, View } from "lucide-react";
import Permiso from "../../router/Permiso";
import { PerfilService } from "../../services/PerfilService";
import { UsuarioService } from "../../services/UsuarioService";
import { MotivosBajaService } from "../../services/MotivosBajaService";
import { toast } from "react-toastify";

const ListaUsuarios = ({ onConsultar, token, debeRecargar, onRecargaCompleta }) => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [filtroBusqueda, setFiltroBusqueda] = useState("");
  const [filtroPerfil, setFiltroPerfil] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [loadingConsulta, setLoadingConsulta] = useState(false);
  const [success, setSuccess] = useState("");
  const [showBajaModal, setShowBajaModal] = useState(false);
  const [showDispositivoModal, setShowDispositivoModal] = useState(false);
  const [dispositivos, setDispositivos] = useState([]);
  const [selectedColaborador, setSelectedColaborador] = useState(null);
  const [motivos, setMotivos] = useState([]);
  const [perfilesPermitidos, setPerfilesPermitidos] = useState([]);
  const [bajaForm, setBajaForm] = useState({
    motivo_baja: "",
    fecha_baja: "",
    comentario: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRegistros, setTotalRegistros] = useState(0);

  // Efecto para filtrar usuarios cuando cambien los filtros
  useEffect(() => {
    cargarPerfiles();
    fetchUsuarios();
    cargarMotivos();
  }, [currentPage, filtroBusqueda, filtroPerfil, filtroEstado]);

  const cargarPerfiles = async () => {
    try {
      const response = await PerfilService.fetchPerfiles(token);
      setPerfilesPermitidos(response);
    } catch (error) {
      console.error("Error al cargar perfiles:", error);
    }
  };

  const cargarMotivos = async () => {
    try {
      const response = await MotivosBajaService.getMotivos(token);
      setMotivos(response);
    } catch (error) {
      console.error("Error al cargar perfiles:", error);
    }
  };

  const fetchUsuarios = async () => {
    setLoading(true);
    setError("");
    try {
      const { usuarios, totalPaginas, totalRegistros } = await UsuarioService.fetchUsuarios({
        token,
        pagina: currentPage,
        pageSize: 20,
        buscar: filtroBusqueda,
        perfil: filtroPerfil,
        estado: filtroEstado,
      });

      setUsuarios(usuarios);
      setTotalPages(totalPaginas);
      setTotalRegistros(totalRegistros);
    } catch (err) {
      console.error("Error al cargar usuarios:", err);
      setError(
        "Error al cargar usuarios: " +
        (err.response?.data?.error || err.message)
      );
      setUsuarios([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debeRecargar) {
      fetchUsuarios().then(() => {
        onRecargaCompleta(); // ← marca como recargado
      });
    }
  }, [debeRecargar]);

  const getEstadoClass = (estado) => {
    const estadoValue = estado || "ACTIVO";
    switch (estadoValue.toUpperCase()) {
      case "ACTIVO":
        return "usuarios-estado-activo";
      case "BAJA":
        return "usuarios-estado-baja";
      default:
        return "usuarios-estado-pendiente";
    }
  };

  // Función para obtener datos completos del usuario
  const handleConsultarUsuario = async (usuario) => {
    setLoadingConsulta(true);
    setError("");

    try {
      const data = await UsuarioService.consultarUsuarioPorId({ token, id: usuario.id });
      onConsultar(data);
    } catch (err) {
      setError("Error al cargar datos del usuario: " + (err.response?.data?.error || err.message));
    } finally {
      setLoadingConsulta(false);
    }
  };

  const handleConsultarUsuarioDispositivo = async (usuario) => {
    setSelectedColaborador(usuario);
    setLoadingConsulta(true);
    setError("");
    try {
      const data = await UsuarioService.consultarDispositivoUsuarioPorId({ token, id: usuario.id });
      console.log(data)
      setDispositivos(data.user.dispositivoRows)
      setShowDispositivoModal(true);
    } catch (err) {
      setError("Error al cargar datos del usuario: " + (err.response?.data?.error || err.message));
    } finally {
      setLoadingConsulta(false);
    }
  };

  const handleBajaColaborador = (colaborador) => {
    setSelectedColaborador(colaborador);
    setBajaForm({
      motivo_baja: "",
      fecha_baja: new Date().toISOString().split("T")[0],
      comentario: "",
    });
    setShowBajaModal(true);
    setError("");
  };

  const handleFormChange = (e) => {
    setBajaForm({
      ...bajaForm,
      [e.target.name]: e.target.value,
    });
  };

  const handleDesactivarDispositivo = async (id_usuario, idDispositivo) => {
    try {
      await UsuarioService.desactivarDispositivoUsuario({
        token,
        idDispositivo: idDispositivo,
        id: id_usuario,
      });
      const actualizados = dispositivos.map((d) =>
        d.id_dispositivo === idDispositivo ? { ...d, estado: 0 } : d
      );
      setDispositivos(actualizados);
      toast.success("Dispositivo desactivado para este usuario")
    } catch (error) {
      console.error("Error al desactivar dispositivo:", error);
      toast.error("No se pudo desactivar el dispositivo.")
    }
  };

  const handleConfirmarBaja = async () => {
    if (!bajaForm.motivo_baja || !bajaForm.fecha_baja) {
      setError("Motivo y fecha de baja son obligatorios");
      return;
    }

    try {
      await UsuarioService.darDeBajaUsuario({
        token,
        id: selectedColaborador.id,
        bajaForm,
      });

      toast.success("Usuario fue dado de baja correctamente")
      setShowBajaModal(false);
      fetchUsuarios(); // Recargar la lista
    } catch (err) {
      setError("Error al dar de baja al usuario");
    }
  };

  const exportarExcel = async () => {
    try {
      const blob = await UsuarioService.exportarUsuariosExcel({
        token,
        filtroBusqueda,
        filtroPerfil,
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "usuarios.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error al exportar Excel:", error);
    }
  };

  return (
    <div className="usuarios-table-container">
      <div className="usuarios-table-header">
        <h2 className="usuarios-table-title">Lista de Usuarios</h2>
        <div className="productos-actions">
          {successMsg && <div className="success-message">{successMsg}</div>}
          {error && <div className="error-message">{error}</div>}
          <Permiso permiso="export_users">
            <button onClick={exportarExcel} className="btn-exportar">
              Exportar a Excel
            </button>
          </Permiso>
        </div>
      </div>

      {/* Filtros de búsqueda */}
      <div className="usuarios-filtros">
        <div className="filtro-item">
          <label htmlFor="filtroBusqueda">Buscar:</label>
          <input
            id="filtroBusqueda"
            type="text"
            placeholder="Nombre, documento, celular, email..."
            value={filtroBusqueda}
            onChange={(e) => setFiltroBusqueda(e.target.value)}
            className="filtro-input"
          />
        </div>

        <div className="filtro-item">
          <label htmlFor="filtroPerfil">Perfil:</label>
          <select
            id="filtroPerfil"
            value={filtroPerfil}
            onChange={(e) => setFiltroPerfil(e.target.value)}
            className="filtro-select"
          >
            <option value="">Todos los perfiles</option>
            {perfilesPermitidos.map((perfil) => (
              <option key={perfil.id} value={perfil.id}>
                {perfil.nombre}
              </option>
            ))}
          </select>
        </div>

        <div className="filtro-item">
          <label htmlFor="filtroEstado">Estado:</label>
          <select
            id="filtroEstado"
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="filtro-select"
          >
            <option value="">Todos los estado</option>
            <option value="ACTIVO">Activo</option>
            <option value="BAJA">Baja</option>
          </select>
        </div>

        <div className="filtro-item">
          <button
            onClick={() => {
              setFiltroBusqueda("");
              setFiltroPerfil("");
              setFiltroEstado("");
            }}
            className="btn-limpiar-filtros"
          >
            Limpiar Filtros
          </button>
        </div>
      </div>

      <div className="usuarios-table-wrapper">
        <table className="usuarios-table">
          <thead>
            <tr>
              <th>Tipo Documento</th>
              <th>Número</th>
              <th>Nombres y Apellidos</th>
              <th>Celular</th>
              <th>Perfil</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="loading-cell">
                  <div className="loading">Cargando usuarios...</div>
                </td>
              </tr>
            ) : usuarios.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">
                  {usuarios.length === 0
                    ? "No hay usuarios registrados"
                    : "No se encontraron usuarios con los filtros aplicados"}
                </td>
              </tr>
            ) : (
              usuarios.map((usuario) => (
                <tr key={usuario.id}>
                  <td>{usuario.tipo_documento || "-"}</td>
                  <td>{usuario.numero_documento || "-"}</td>
                  <td>{usuario.nombres } {usuario.apellidoPaterno}  {usuario.apellidoMaterno}</td>
                  <td>{usuario.celular || "-"}</td>
                  <td>{usuario.perfil_nombre || "-"}</td>
                  <td>
                    <span
                      className={`usuarios-estado ${getEstadoClass(
                        usuario.estado
                      )}`}
                    >
                      {usuario.estado}
                    </span>
                  </td>
                  <td>
                    <Permiso permiso="user_list_dispositivo">
                      {usuario.estado !== "BAJA" && (
                        <span
                          style={{ cursor: "pointer" }}
                          onClick={() => handleConsultarUsuarioDispositivo(usuario)}
                          disabled={loadingConsulta}
                        >
                          <Smartphone size={20} style={{ marginRight: "6px" }} />
                        </span>
                      )}
                    </Permiso>
                    <Permiso permiso="view_user">
                      {usuario.estado !== "BAJA" && (
                        <span
                          style={{ cursor: "pointer" }}
                          onClick={() => handleConsultarUsuario(usuario)}
                          disabled={loadingConsulta}
                        >
                          <View size={20} style={{ marginRight: "6px" }} />
                        </span>
                      )}
                    </Permiso>
                    {usuario.estado !== "BAJA" && (
                      <Permiso permiso="delete_user">
                        <span onClick={() => handleBajaColaborador(usuario)}>
                          <ToggleLeft size={22} color="#e74c3c" />
                        </span>
                      </Permiso>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <span style={{ marginLeft: "12px", fontSize: "14px", color: "#555" }}>
        {" "}
        Total de registros: {totalRegistros}{" "}
      </span>
      <div className="pagination">
        <button
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Anterior
        </button>
        <span>
          Página {currentPage} de {totalPages}
        </span>

        <button
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Siguiente
        </button>
      </div>

      {/* Sub-tarjeta para baja de colaborador */}
      {showBajaModal && selectedColaborador && (
        <div className="sub-card-overlay">
          <div className="sub-card">
            <div className="sub-card-header">
              <h3>Baja del Usuario</h3>
              <button
                className="sub-card-close"
                onClick={() => setShowBajaModal(false)}
              >
                ×
              </button>
            </div>

            <div className="sub-card-content">
              <div className="colaborador-info">
                <div className="info-row">
                  <span className="">
                    {selectedColaborador.nombres}{" "}
                    {selectedColaborador.apellidos}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">
                    {selectedColaborador.tipo_documento} :
                  </span>
                  <span className="info-value">
                    {selectedColaborador.numero_documento}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Región:</span>
                  <span className="info-value">
                    {selectedColaborador.region_nombre || "-"}
                  </span>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="motivo-baja">Motivo de la Baja:</label>
                <select
                  name="motivo_baja"
                  value={bajaForm.motivo_baja}
                  onChange={handleFormChange}
                  className="sub-card-select"
                  required
                >
                  <option value="">Seleccionar motivo</option>
                  {motivos.map((motivo) => (
                    <option key={motivo.id} value={motivo.id}>
                      {motivo.descripcion}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="fecha-baja">Fecha de Baja:</label>
                <input
                  type="date"
                  name="fecha_baja"
                  value={bajaForm.fecha_baja}
                  onChange={handleFormChange}
                  className="sub-card-input"
                  required
                  readOnly // Fecha de baja no debe cambiar una vez registrada
                />
              </div>

              <div className="form-group">
                <label htmlFor="comentario">Comentario:</label>
                <textarea
                  name="comentario"
                  value={bajaForm.comentario}
                  onChange={handleFormChange}
                  className="sub-card-textarea"
                  rows="3"
                  placeholder="Ingrese comentarios adicionales..."
                />
              </div>

              {error && <div className="sub-card-error">{error}</div>}
              {success && <div className="sub-card-success">{success}</div>}
            </div>

            <div className="sub-card-actions">
              <button
                className="sub-card-btn sub-card-btn-secondary"
                onClick={() => setShowBajaModal(false)}
              >
                Cancelar
              </button>
              <button
                className="sub-card-btn sub-card-btn-danger"
                onClick={handleConfirmarBaja}
              >
                Confirmar Baja
              </button>
            </div>
          </div>
        </div>
      )}

      {showDispositivoModal && selectedColaborador && (
        <div className="sub-card-overlay">
          <div className="sub-card">
            <div className="sub-card-header">
              <h3>Dispositivo asociados</h3>
              <button
                className="sub-card-close"
                onClick={() => setShowDispositivoModal(false)}
              >
                ×
              </button>
            </div>

            <div className="sub-card-body" >
              {dispositivos.length === 0 ? (
                <p>No hay dispositivos registrados para este usuario.</p>
              ) : (
                <div className="productos-table-wrapper">
                  <table className="productos-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Marca</th>
                        <th>Modelo</th>
                        <th>SO</th>
                        <th>Versión</th>
                        <th>Estado</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dispositivos.map((d) => (
                        <tr key={d.id_dispositivo}>
                          <td>{d.id_dispositivo}</td>
                          <td>{d.brand}</td>
                          <td>{d.modelName}</td>
                          <td>{d.osName}</td>
                          <td>{d.osVersion}</td>
                          <td>{d.estado === 1 ? "Activo" : "Desactivado"}</td>
                          <td>
                            {d.estado === 1 && (
                              <span
                                onClick={() => handleDesactivarDispositivo(d.id_usuario, d.id_dispositivo)}
                                title="Desactivar dispositivo"
                              >
                                <ToggleLeft size={22} color="#e74c3c" />
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>



            <div className="sub-card-actions">
              <button
                className="sub-card-btn sub-card-btn-secondary"
                onClick={() => setShowDispositivoModal(false)}
              >
                Cerrar
              </button>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListaUsuarios;
